import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(localStorage.getItem('profilePhoto'));
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('profilePhoto');
    setToken(null);  // Update state after logout
    setProfilePhoto(null);
    setIsDropdownOpen(false); // Close the dropdown menu
    navigate('/');  // Redirect to home page after logout
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setToken(localStorage.getItem('token')); // Reactively update token state
      setProfilePhoto(localStorage.getItem('profilePhoto'));
    };

    // Listen for storage updates (in case token or profile photo changes from another tab)
    window.addEventListener('storage', handleStorageChange);

    // Close dropdown if clicked outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
        <div className="flex">
          <Link to="/" className="flex-shrink-0 flex items-center">
            <img
              src="/Logo.png" 
              alt="Alfa Entrepreneur Logo"
              className="h-30 w-60"
            />
          </Link>
        </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            <Link to="/articles" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">Articles</Link>
            <Link to="/newsletter" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">Newsletter</Link>
            <Link to="/podcast" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">Podcast</Link>
            
            {!token ? (
              <div>
                <Link to="/login" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">Login</Link>
                <Link to="/signup" className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700">
                  Become a Member
                </Link>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    className="h-8 w-8 rounded-full cursor-pointer"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  />
                ) : (
                  <div
                    className="h-8 w-8 rounded-full bg-gray-200 cursor-pointer flex items-center justify-center"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span className="text-gray-500">No Photo</span>
                  </div>
                )}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                    <Link to="/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">Profile</Link>
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/articles" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">Articles</Link>
            <Link to="/newsletter" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">Newsletter</Link>
            <Link to="/podcast" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">Podcast</Link>
            
            {!token ? (
              <>
                <Link to="/login" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">Login</Link>
                <Link to="/signup" className="block px-3 py-2 rounded-md text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700">Become a Member</Link>
              </>
            ) : (
              <>
                <Link to="/profile" className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">Profile</Link>
                <button
                  onClick={logout}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  Log Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}